import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  signal
} from '@angular/core';
import { BadgeComponent, ButtonComponent, IconComponent, InputComponent, TranslatePipe, translator } from '@softline/ui-core';
import { PageFilterService } from '../../../services/page-filter.service';
import { FilterTemplateService } from '../../../../services/filter-template.service';
import { FilterTemplate } from '../../../../types/filter-template';
import { PageFilterComponentBase } from '../page-filter-component-base';

@Component({
  selector: 'soft-page-filter-templates',
  imports: [
    BadgeComponent,
    ButtonComponent,
    IconComponent,
    InputComponent,
    TranslatePipe
  ],
  templateUrl: './page-filter-templates.component.html',
  styleUrl: './page-filter-templates.component.css',
  providers: [
    {provide: PageFilterComponentBase, useExisting: forwardRef(() => PageFilterTemplatesComponent)}
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageFilterTemplatesComponent extends PageFilterComponentBase {
  private readonly t = translator();

  readonly service = inject(PageFilterService);
  readonly templateService = inject(FilterTemplateService);

  readonly newName = signal<string | null>(null);
  readonly renamingId = signal<string | null>(null);
  readonly renameValue = signal<string | null>(null);

  override title = computed(() => this.t('#CORE_FILTER.TEMPLATES.TITLE')());
  override canBack = computed(() => true);
  override canApply = computed(() => true);
  override tabsVisible = computed(() => false);

  constructor() {
    super();
    this.templateService.load().catch(e => console.error('[PageFilterTemplatesComponent] load failed', e));
  }

  override back = () => {
    this.service.view.set('filter');
  }

  override apply = () => {
    this.service.applyFilter();
    this.service.opened.set(false);
    this.service.view.set('filter');
  }

  async create(): Promise<void> {
    const name = this.newName()?.trim();
    if(!name)
      return;
    try {
      await this.templateService.createFromDraft(name);
      this.newName.set(null);
    } catch (e) {
      console.error('[PageFilterTemplatesComponent] create failed', e);
    }
  }

  applyTemplate(template: FilterTemplate): void {
    this.templateService.applyToDraft(template);
    this.service.view.set('filter');
  }

  async duplicate(template: FilterTemplate): Promise<void> {
    try {
      await this.templateService.duplicate(template.id, `${this.t('#CORE_FILTER.TEMPLATES.COPY_SUFFIX')()}`);
    } catch (e) {
      console.error('[PageFilterTemplatesComponent] duplicate failed', e);
    }
  }

  startRename(template: FilterTemplate): void {
    this.renamingId.set(template.id);
    this.renameValue.set(template.name);
  }

  async confirmRename(): Promise<void> {
    const id = this.renamingId();
    const name = this.renameValue()?.trim();
    if(!id || !name) {
      this.cancelRename();
      return;
    }
    try {
      await this.templateService.rename(id, name);
    } catch (e) {
      console.error('[PageFilterTemplatesComponent] rename failed', e);
    }
    this.cancelRename();
  }

  cancelRename(): void {
    this.renamingId.set(null);
    this.renameValue.set(null);
  }

  async toggleFavorite(template: FilterTemplate): Promise<void> {
    try {
      await this.templateService.toggleFavorite(template.id);
    } catch (e) {
      console.error('[PageFilterTemplatesComponent] toggleFavorite failed', e);
    }
  }

  async remove(template: FilterTemplate): Promise<void> {
    try {
      await this.templateService.remove(template.id);
    } catch (e) {
      console.error('[PageFilterTemplatesComponent] remove failed', e);
    }
  }
}
